/**
 * The platform's one temporal vocabulary, on the frontend side.
 *
 * `tricorder-go/temporal/doc.go` is the authority and
 * `specs/api/api-service.overlay.d/04-temporal.yaml` is the wire declaration.
 * Five properties cover every temporal field the platform has — `created`,
 * `updated`, `occurred`, `period` and `sequence` — and `period` is the only one
 * of them that is an interval, so it is declared once here. A second
 * declaration is a second vocabulary, and the two drift. Every endpoint module
 * that carries an interval reads it from here: `endpoints/skills.ts`,
 * `endpoints/teamSkills.ts`, `endpoints/agentRuns.ts` and `endpoints/teams.ts`.
 */

import { z } from 'zod';

/**
 * A half-open interval of instants, `[start, end)`. The start is in and the end
 * is out, which is what lets one interval end exactly where the next begins
 * with no gap and no double count.
 *
 * `end` null means the period is open: in force, with no end known or planned.
 * It never means unbounded in the past, and it is a claim about the period
 * rather than a missing value, so it is `.nullable()` and never `.optional()`.
 * A response always writes the key.
 */
export const PeriodSchema = z.object({
  start: z.string(),
  end: z.string().nullable(),
});

export type Period = z.infer<typeof PeriodSchema>;
